import { CookieMixins } from '@/mixins/cookie';
import OrderService from '@/api/services/OrderService';

const { methods: Cookie } = CookieMixins;

export const namespaced = true;

export const state = {
  order: {},
  localItems: [],
  loading: false,
  itemLoading: false,
  newItemsAddition: [],
};

export const mutations = {
  SET_ORDER(state, order) {
    state.order = order;
    window.AnalyticsFunnels?.onOrderLoaded(order);
  },
  SET_LOCAL_ITEMS(state, items) {
    state.localItems = items;
    Cookie.setBasketCookie(items);
  },
  STORE_LOCAL_ITEMS(state, items) {
    items = [...state.localItems, ...items];
    state.localItems = items;
    Cookie.setBasketCookie(items);
  },
  LOADING(state, loading) {
    state.loading = loading;
  },
  ITEMLOADING(state, loading) {
    state.itemLoading = loading;
  },
  ADDING_NEW_ITEMS(state, itemProductIds) {
    state.newItemsAddition = [...state.newItemsAddition, ...itemProductIds];
  },
  REMOVE_ADDING_NEW_ITEMS(state, itemProductIds) {
    itemProductIds.forEach((id) => {
      const index = state.newItemsAddition.indexOf(id);
      if (index > -1) {
        state.newItemsAddition.splice(index, 1);
      }
    });
  },
};

export const actions = {
  fetchOrder({ commit, dispatch, rootGetters }, { orderId }) {
    commit('LOADING', true);
    return new Promise((resolve, reject) =>
      OrderService.getOrder(orderId)
        .then((response) => {
          commit('SET_ORDER', response);
          commit('LOADING', false);
          if (response.itemsCount > 0)
            dispatch(
              'notification/notifySuccess',
              {
                message: `${response.itemsCount} ${response.itemsCount > 1 ? 'Items' : 'Item'}. <a href="${rootGetters.generateFullUrl(
                  '/basket'
                )}" class="text-primary"><u>View Basket</u></a> | <a href="${rootGetters.generateFullUrl(
                  `/order/${orderId}/checkout`
                )}" class="text-primary"><u>Checkout</u></a>`,
                options: {
                  family: 'AddItem',
                  overrideDuplicate: false, // If same family notification exists then it will ignore this
                  icon: null,
                  dismissSecs: -1,
                  dismissible: true,
                  isHtmlMessage: true,
                  class: 'text-center',
                  text: { class: 'text-secondary' },
                },
              },
              { root: true }
            );
          resolve();
        })
        .catch((error) => {
          commit('SET_ORDER', {});
          commit('LOADING', false);
          if (error) error.location = 'fetchOrder';
          dispatch(
            'notification/notifyFailure',
            { message: 'Fail to retrieve your order info.', options: { dismissSecs: 15 } },
            { root: true }
          );
          reject(error);
        })
    );
  },
  uploadExportedDesigns({ dispatch, commit }, { tagFront, laserFront, svgLaserFront }) {
    commit('ITEMLOADING', true);
    return new Promise((resolve, reject) =>
      Promise.all([
        OrderService.uploadCustomDesignFront(null, tagFront),
        OrderService.uploadLaserCustomDesignFront(null, laserFront),
        OrderService.uploadLaserCustomDesignFrontSvg(null, svgLaserFront),
      ])
        .then((responses) => resolve({ tagFront: responses[0]?.data?.id, laserFront: responses[1]?.data?.id, svgLaserFront: responses[2]?.data?.id}))
        .catch((error) => {
          error.location = 'customDesignUploads';
          dispatch(
            'notification/notifyFailure',
            { message: 'Failed to upload design exports.', options: { dismissSecs: 15 } },
            { root: true }
          ).then(reject);
        })
        .finally(() => {
          commit('ITEMLOADING', false);
        })
    );
  },
  uploadWalletCardDesigns({ dispatch, commit }, { orderItemId, frontDesign, backDesign }) {
    commit('ITEMLOADING', true);
    return new Promise((resolve, reject) =>
      OrderService.uploadWalletCardDesigns(orderItemId, frontDesign, backDesign)
        .then((response) => {
          resolve(response);
        })
        .catch((error) => {
          if (error) error.location = 'customWalletCardUploads';
          dispatch(
            'notification/notifyFailure',
            { message: 'Failed to upload wallet card exports.', options: { dismissSecs: 15 } },
            { root: true }
          );
          reject(error);
        })
        .finally(() => {
          commit('ITEMLOADING', false);
        })
    );
  },
  storeLocalItems({ commit, dispatch, getters, rootGetters }, { data, quantity = 1, notificationOptions }) {
    const itemProductIds = data?.filter((item) => item?.productId)?.map((item) => item.productId);
    commit('ADDING_NEW_ITEMS', itemProductIds);
    for (let i = 0; i < quantity; i++) {
      commit('STORE_LOCAL_ITEMS', data);
    }
    if (getters.getLocalCount > 0)
      dispatch(
        'notification/notifySuccess',
        {
          message: `${getters.getLocalCount} ${getters.getLocalCount > 1 ? 'Items' : 'Item'}. <a href="${rootGetters.generateFullUrl(
            '/basket'
          )}" class="text-primary"><u>View Basket</u></a> | <a href="${rootGetters.generateFullUrl(
            `/login/store?redirect_path=%2Fstore`
          )}" class="text-primary"><u>Checkout</u></a>`,
          options: {
            family: 'AddItem',
            overrideDuplicate: false, // If same family notification exists then it will ignore this
            icon: null,
            dismissSecs: -1,
            dismissible: true,
            isHtmlMessage: true,
            class: 'text-center',
            text: { class: 'text-secondary' },
            ...notificationOptions,
          },
        },
        { root: true }
      );
    commit('REMOVE_ADDING_NEW_ITEMS', itemProductIds);
  },
  addLocalItems({ commit, dispatch }, { vm, orderId, items }) {
    commit('LOADING', true);
    const itemProductIds = items?.filter((item) => item?.productId)?.map((item) => item.productId);
    commit('ADDING_NEW_ITEMS', itemProductIds);
    return new Promise((resolve, reject) =>
      OrderService.addItems(orderId, items)
        .then(async () => {
          await dispatch('fetchOrder', { vm, orderId });
          resolve();
        })
        .catch((error) => {
          error.location = 'addItems';
          dispatch(
            'notification/notifyFailure',
            { message: 'Failed to add your local items.', options: { dismissSecs: 15 } },
            { root: true }
          );
          reject();
        })
        .finally(() => {
          commit('LOADING', false);
          commit('REMOVE_ADDING_NEW_ITEMS', itemProductIds);
        })
    );
  },
  removeItem(context, { orderId, itemId }) {
    return new Promise((resolve, reject) =>
      OrderService.removeItem(orderId, itemId)
        .then((response) => resolve(response))
        .catch((error) => {
          error.location = 'removeItem';
          reject();
        })
    );
  },
  addReplacementTagItem({ commit, dispatch, state, rootGetters }, { vm, orderId, productDetails, fetchOrderOnSuccess }) {
    commit('ITEMLOADING', true);

    return new Promise((resolve, reject) =>
      OrderService.addReplacementTagItem(orderId, productDetails)
        .then((response) => {
          commit('SET_ORDER', response);
          dispatch(
            'notification/notifySuccess',
            {
              message: `Item added. <br class="d-block d-md-none" /><a href="${rootGetters.generateFullUrl(
                '/basket'
              )}" class="text-primary">View Basket</a> | <a href="${rootGetters.generateFullUrl(
                `/order/${orderId}/checkout`
              )}" class="text-primary">Checkout</a>`,
              options: {
                family: 'AddItem',
                dismissSecs: 5,
                dismissible: true,
                isHtmlMessage: true,
                class: 'text-center',
                text: { class: 'text-secondary' },
                $callback: { generateFullUrl: rootGetters.generateFullUrl, order: state.order, notifySuccess: vm.notifySuccess },
                callback: function ({ order, generateFullUrl, notifySuccess }) {
                  notifySuccess(
                    `${order.itemsCount} ${order.itemsCount > 1 ? 'Items' : 'Item'}. <a href="${generateFullUrl(
                      '/basket'
                    )}" class="text-primary"><u>View Basket</u></a> | <a href="${generateFullUrl(
                      `/order/${orderId}/checkout`
                    )}" class="text-primary"><u>Checkout</u></a>`,
                    {
                      family: 'AddItem',
                      icon: null,
                      dismissSecs: -1,
                      dismissible: true,
                      isHtmlMessage: true,
                      class: 'text-center',
                      text: { class: 'text-secondary' },
                    }
                  );
                },
              },
            },
            { root: true }
          );
          if (fetchOrderOnSuccess) {
            dispatch('fetchOrder', { vm, orderId });
          }
          resolve();
        })
        .catch((error) => {
          error.location = 'addItem';
          dispatch(
            'notification/notifyFailure',
            { message: 'Failed to add this product selection. Try again.', options: { dismissSecs: 15 } },
            { root: true }
          );
          reject();
        })
        .finally(() => commit('ITEMLOADING', false))
    );
  },
  async addProductBundle({ commit, dispatch, state, rootGetters }, { vm, orderId, tagProductId, petId, personalization }) {
    try {
      commit('ITEMLOADING', true);
      commit('SET_ORDER', await OrderService.addProductBundle(orderId, tagProductId, petId, personalization));
      dispatch(
        'notification/notifySuccess',
        {
          message: `Bundle added. <br class="d-block d-md-none" /><a href="${rootGetters.generateFullUrl(
            '/basket'
          )}" class="text-primary">View Basket</a> | <a href="${rootGetters.generateFullUrl(
            `/order/${orderId}/checkout`
          )}" class="text-primary">Checkout</a>`,
          options: {
            family: 'AddItem',
            dismissSecs: 5,
            dismissible: true,
            isHtmlMessage: true,
            class: 'text-center',
            text: { class: 'text-secondary' },
            $callback: {
              generateFullUrl: rootGetters.generateFullUrl,
              order: state.order,
              notifySuccess: vm.notifySuccess,
            },
            callback: function ({ order, generateFullUrl, notifySuccess }) {
              notifySuccess(
                `${order.itemsCount} ${order.itemsCount > 1 ? 'Items' : 'Item'}. <a href="${generateFullUrl(
                  '/basket'
                )}" class="text-primary"><u>View Basket</u></a> | <a href="${generateFullUrl(
                  `/order/${orderId}/checkout`
                )}" class="text-primary"><u>Checkout</u></a>`,
                {
                  family: 'AddItem',
                  icon: null,
                  dismissSecs: -1,
                  dismissible: true,
                  isHtmlMessage: true,
                  class: 'text-center',
                  text: { class: 'text-secondary' },
                }
              );
            },
          },
        },
        { root: true }
      );
    } catch (error) {
      error.location = 'addItem';
      dispatch(
        'notification/notifyFailure',
        { message: 'Failed to add this product selection. Try again.', options: { dismissSecs: 15 } },
        { root: true }
      );
    } finally {
      commit('ITEMLOADING', false);
    }
  },
  async updateProductBundle({ commit, dispatch }, { orderId, orderItemBundleId, tagProductId, petId, personalization }) {
    try {
      commit('ITEMLOADING', true);
      commit('SET_ORDER', await OrderService.updateProductBundle(orderId, orderItemBundleId, tagProductId, petId, personalization));
    } catch (error) {
      error.location = 'updateProductBundle';
      dispatch(
        'notification/notifyFailure',
        { message: 'Failed to update this product bundle. Try again.', options: { dismissSecs: 15 } },
        { root: true }
      );
      commit('ITEMLOADING', false);
    }

    // NOTE: Loading state intentionally remains active until page redirection completes.
  },
  addItem({ commit, dispatch, state, rootGetters }, { vm, orderId, productDetails, fetchOrderOnSuccess }) {
    commit('ITEMLOADING', true);

    return new Promise((resolve, reject) =>
      OrderService.addItem(orderId, productDetails)
        .then((response) => {
          commit('SET_ORDER', response);
          dispatch(
            'notification/notifySuccess',
            {
              message: `Item added. <br class="d-block d-md-none" /><a href="${rootGetters.generateFullUrl(
                '/basket'
              )}" class="text-primary">View Basket</a> | <a href="${rootGetters.generateFullUrl(
                `/order/${orderId}/checkout`
              )}" class="text-primary">Checkout</a>`,
              options: {
                family: 'AddItem',
                dismissSecs: 5,
                dismissible: true,
                isHtmlMessage: true,
                class: 'text-center',
                text: { class: 'text-secondary' },
                $callback: { generateFullUrl: rootGetters.generateFullUrl, order: state.order, notifySuccess: vm.notifySuccess },
                callback: function ({ order, generateFullUrl, notifySuccess }) {
                  notifySuccess(
                    `${order.itemsCount} ${order.itemsCount > 1 ? 'Items' : 'Item'}. <a href="${generateFullUrl(
                      '/basket'
                    )}" class="text-primary"><u>View Basket</u></a> | <a href="${generateFullUrl(
                      `/order/${orderId}/checkout`
                    )}" class="text-primary"><u>Checkout</u></a>`,
                    {
                      family: 'AddItem',
                      icon: null,
                      dismissSecs: -1,
                      dismissible: true,
                      isHtmlMessage: true,
                      class: 'text-center',
                      text: { class: 'text-secondary' },
                    }
                  );
                },
              },
            },
            { root: true }
          );
          if (fetchOrderOnSuccess) {
            dispatch('fetchOrder', { vm, orderId });
          }
          resolve();
        })
        .catch((error) => {
          error.location = 'addItem';
          dispatch(
            'notification/notifyFailure',
            { message: 'Failed to add this product selection. Try again.', options: { dismissSecs: 15 } },
            { root: true }
          );
          reject();
        })
        .finally(() => commit('ITEMLOADING', false))
    );
  },
  removeWalletCard({ commit, dispatch }, input) {
    const removeFromBasket = input.removeFromBasket || null;
    delete input.removeFromBasket;
    removeFromBasket
      ? (commit('basket/REMOVEITEMLOADING', true, { root: true }), commit('basket/REMOVEITEMID', input.petId, { root: true }))
      : commit('LOADING', true);

    return new Promise((resolve, reject) =>
      OrderService.removeWalletCard(input)
        .then((response) => {
          commit('SET_ORDER', response);
          removeFromBasket
            ? (commit('basket/REMOVEITEMID', null, { root: true }),
              commit('basket/REMOVEITEMERROR', false, { root: true }),
              commit('basket/SHOW_BASKET', true, { root: true }))
            : null;
          resolve(response);
        })
        .catch((error) => {
          removeFromBasket ? commit('basket/REMOVEITEMERROR', true, { root: true }) : null;
          const notification = {
            type: 'error',
            message: error.message,
          };
          dispatch('notification/add', notification, { root: true });
          reject(error);
        })
        .finally(() => (removeFromBasket ? commit('basket/REMOVEITEMLOADING', false, { root: true }) : commit('LOADING', false)))
    );
  },
  orderNoTag({ commit, dispatch }, { orderId, petId }) {
    commit('ITEMLOADING', true);

    return new Promise((resolve, reject) =>
      OrderService.orderNoTag(orderId, petId)
        .then(() =>
          dispatch('fetchOrder', { orderId }).then(() => {
            dispatch('notification/notifySuccess', { message: 'Successfully skipped this pet.' }, { root: true });
            resolve();
          })
        )
        .catch((error) => {
          error.location = 'orderNoTag';
          dispatch('notification/notifyFailure', { message: 'Failed to skip. Try again.', options: { dismissSecs: 15 } }, { root: true });
          reject(error);
        })
        .finally(() => commit('ITEMLOADING', false))
    );
  },
  assignItem: ({ commit, dispatch }, { orderId, payloadArray }) =>
    new Promise((resolve, reject) => {
      commit('LOADING', true);
      commit('ITEMLOADING', true);
      OrderService.assignItem(orderId, payloadArray)
        .then(() => {
          dispatch('fetchOrder', { orderId }).then(() => {
            dispatch('notification/notifySuccess', { message: 'Basket updated.' }, { root: true });
            resolve();
          });
        })
        .catch((error) => {
          error.location = 'assignItem';
          dispatch(
            'notification/notifyFailure',
            { message: 'Failed to update basket. Try again.', options: { dismissSecs: 15 } },
            { root: true }
          );
          reject();
        })
        .finally(() => {
          commit('LOADING', false);
          commit('ITEMLOADING', false);
        });
    }),
  upgradeToPriorityShipping: ({ commit, dispatch }, orderId) =>
    new Promise((resolve, reject) => {
      commit('LOADING', true);
      commit('ITEMLOADING', true);
      OrderService.upgradeToPriorityShipping(orderId)
        .then((response) => {
          commit('SET_ORDER', response);
          dispatch('notification/notifySuccess', { message: 'Successfully upgraded to Priority & Tracked Shipping.' }, { root: true });
          resolve();
        })
        .catch((error) => {
          error.location = 'orderShipping';
          dispatch(
            'notification/notifyFailure',
            { message: 'Failed to upgrade to priority & tracked shipping. Try again.', options: { dismissSecs: 15 } },
            { root: true }
          );
          reject();
        })
        .finally(() => {
          commit('LOADING', false);
          commit('ITEMLOADING', false);
        });
    }),
  downgradeToRegularShipping: ({ commit, dispatch }, orderId) =>
    new Promise((resolve, reject) => {
      commit('LOADING', true);
      commit('ITEMLOADING', true);
      OrderService.downgradeToRegularShipping(orderId)
        .then((response) => {
          commit('SET_ORDER', response);
          dispatch('notification/notifySuccess', { message: 'Successfully downgraded to Regular Mail Shipping (Free).' }, { root: true });
          resolve();
        })
        .catch((error) => {
          error.location = 'orderShipping';
          dispatch(
            'notification/notifyFailure',
            { message: 'Failed to downgrade to Regular Mail Shipping. Try again.', options: { dismissSecs: 15 } },
            { root: true }
          );
          reject();
        })
        .finally(() => {
          commit('LOADING', false);
          commit('ITEMLOADING', false);
        });
    }),
};

export const getters = {
  getLocalCount: (state) => {
    return [...state?.localItems]?.length;
  },
  getRequiredPet: (state, getters) => {
    return getters.getRequiredPetByPetId();
  },
  getRequiredPetByPetId:
    (state) =>
    (petId, petLoop = true) => {
      if (!petLoop && !petId) return null;
      const pets = {};
      if (state.order && state.order.groups) {
        state.order.groups.forEach((group) => {
          if (group.pet && group.pet?.id) {
            const petId = group.pet.id;
            pets[petId] = {
              ...group.pet,
              idCode: group.pet.idCode || '',
              licensing: false,
              hasTag: false,
              hasPreviousTag: group.pet.tags && group.pet.tags?.filter(({ municipalTag }) => !municipalTag).length > 0,
              hasPreviousMunicipalTag:
                group.pet.tags &&
                group.pet.tags
                  ?.map(({ id, municipalTag }) =>
                    !municipalTag ? false : group.pet.petTags?.find((tag) => tag?.product?.id === id)?.isValidInCurrentCity
                  )
                  ?.some((value) => value === true),
              walletCardOptedIn: group?.walletCardConfigurations?.some(({ optedIn }) => !!optedIn),
              useReplacementTagInLicensingFlow: group.pet.useReplacementTagInLicensingFlow,
            };
            group?.items?.forEach((item) => {
              if (item.product.type === 'LICENCE') {
                pets[petId].licensing = true;
              } else if (['DESIGNER_TAG', 'PET_TAG'].includes(item.product.type)) {
                pets[petId].hasTag = true;
                if (item.product.municipalTag) {
                  pets[petId].hasMunicipalTag = true;
                }
              }
            });
          }
        });
        return Object.values(pets)?.find((pet) => (petId ? pet.id === petId : pet.licensing && !pet.hasTag));
      }
      return null;
    },
  // Check if all pets with licenses have a tag assigned and/or tags should have a pet assigned
  isValid: (state) => {
    let allTagsAssigned = true;
    const petsWithLicense = {};
    state.order.items?.forEach((item) => {
      if (item.product?.type === 'LICENCE') {
        petsWithLicense[item.group?.pet?.id] = null;
      }
    });
    if (Object.keys(petsWithLicense).length === 0) {
      return true;
    }
    state.order.items.forEach((item) => {
      if (['DESIGNER_TAG', 'PET_TAG'].includes(item.product?.type)) {
        if (item.group?.pet?.id) {
          delete petsWithLicense[item.group?.pet?.id];
          allTagsAssigned = true;
        } else allTagsAssigned = false;
      }
    });
    return allTagsAssigned && Object.keys(petsWithLicense).length === 0;
  },
  // Check if any pet in the order has license product
  somePetsWithLicense: (state) => !!state.order?.items?.some((item) => item.product?.type === 'LICENCE'),
};
